import { z } from "zod";

// TES-381 — patient body photos (e.g. the optional "helkroppsbild" on the
// milestone check-in). The photo rides the API as base64 JSON — no multipart,
// no external object store for MVP; bytes live in Postgres (PatientPhoto.data).
//
// `context` ties the photo to where it was captured: 'milestone:<monthIndex>'
// for the month-N check-in, 'freeform' otherwise. Free string (validated for
// length only) so new capture points don't need a contracts release.

// Decoded (binary) size cap. The client resizes to ≤1280px @ quality 0.7 so a
// real upload lands well under this; the cap is the server-side guarantee.
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export const photoMimeTypeSchema = z.enum(["image/jpeg", "image/png"]);
export type PhotoMimeType = z.infer<typeof photoMimeTypeSchema>;

export const uploadPhotoSchema = z.object({
  context: z.string().min(1).max(120),
  mimeType: photoMimeTypeSchema,
  // Raw base64 (no data-URI prefix). Length-capped at the JSON parser level
  // (main.ts mounts a larger-limit parser on the photos route) and the decoded
  // bytes are re-checked against PHOTO_MAX_BYTES server-side.
  dataBase64: z.string().min(1),
});
export type UploadPhotoInput = z.infer<typeof uploadPhotoSchema>;

export const uploadPhotoResponseSchema = z.object({
  id: z.string().uuid(),
});
export type UploadPhotoResponse = z.infer<typeof uploadPhotoResponseSchema>;

// Metadata-only DTO — the list endpoints NEVER carry the bytes; those are
// served by the dedicated per-photo bytes endpoint with a proper Content-Type.
export const patientPhotoMetaSchema = z.object({
  id: z.string().uuid(),
  context: z.string(),
  mimeType: photoMimeTypeSchema,
  byteSize: z.number().int(),
  createdAt: z.string(),
});
export type PatientPhotoMeta = z.infer<typeof patientPhotoMetaSchema>;

export const patientPhotoListSchema = z.object({
  photos: z.array(patientPhotoMetaSchema),
});
export type PatientPhotoList = z.infer<typeof patientPhotoListSchema>;

// TES-1379 (09B/09C) — the patient's PRIVATE progress photos under "Din hälsa
// över tid". A separate resource from the doctor-addressed photos above: its
// own table (ProgressPhoto), its own patient-only routes
// (/patient-api/v1/progress-photos), and no staff reader. The same image
// rules apply — JPEG/PNG, PHOTO_MAX_BYTES, EXIF stripped server-side.
export const uploadProgressPhotoSchema = z.object({
  mimeType: photoMimeTypeSchema,
  dataBase64: z.string().min(1),
});
export type UploadProgressPhotoInput = z.infer<
  typeof uploadProgressPhotoSchema
>;

// Metadata only, newest first; `createdAt` is the date the app shows. The
// bytes are served by GET /patient-api/v1/progress-photos/:id.
export const progressPhotoMetaSchema = z.object({
  id: z.string().uuid(),
  mimeType: photoMimeTypeSchema,
  byteSize: z.number().int(),
  createdAt: z.string(),
});
export type ProgressPhotoMeta = z.infer<typeof progressPhotoMetaSchema>;

export const progressPhotoListSchema = z.object({
  photos: z.array(progressPhotoMetaSchema),
});
export type ProgressPhotoList = z.infer<typeof progressPhotoListSchema>;
